import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Plus, PencilSimple, Trash, X } from '@phosphor-icons/react';
import { ADMIN } from '@/constants/testIds';

const empty = { name: '', description: '', image_url: '', icon: '', sort_order: 0, is_active: true };

export default function AdminCategories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    api.get('/categories', { params: { active_only: false } }).then((r) => setCategories(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openCreate = () => { setEditing(null); setForm(empty); setShowForm(true); };
  const openEdit = (c) => { setEditing(c); setForm({ name: c.name, description: c.description, image_url: c.image_url, icon: c.icon, sort_order: c.sort_order, is_active: c.is_active }); setShowForm(true); };

  const set = (k) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : (e.target.type === 'number' ? parseInt(e.target.value || 0, 10) : e.target.value);
    setForm({ ...form, [k]: v });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name) { toast.error('Name required'); return; }
    setBusy(true);
    try {
      if (editing) { await api.put(`/categories/${editing.id}`, form); toast.success('Updated'); }
      else { await api.post('/categories', form); toast.success('Created'); }
      setShowForm(false); load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete category "${c.name}"?`)) return;
    try { await api.delete(`/categories/${c.id}`); toast.success('Deleted'); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Delete failed'); }
  };

  const inputCls = 'w-full h-10 px-3 rounded-lg bg-white border border-stone-200 focus:border-olive-400 focus:outline-none text-sm';

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-3xl text-stone-900">Categories</h1>
        <button data-testid={ADMIN.addCategoryBtn} onClick={openCreate} className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-terracotta-600 text-white text-sm font-semibold hover:bg-terracotta-700"><Plus size={16} weight="bold"/> Add Category</button>
      </div>

      {loading ? <p className="text-sm text-stone-500">Loading…</p> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {categories.map((c) => (
            <div key={c.id} className="bg-white border border-stone-200 rounded-2xl p-4 flex items-center gap-3">
              <img src={c.image_url || '/placeholder.png'} alt="" className="w-14 h-14 rounded-xl object-cover bg-stone-100"/>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-stone-900 truncate">{c.name}</p>
                <p className="text-xs text-stone-500 truncate">{c.slug}</p>
                {!c.is_active && <span className="text-[10px] text-stone-500">Hidden</span>}
              </div>
              <button data-testid={ADMIN.categoryEditBtn(c.id)} onClick={() => openEdit(c)} className="w-8 h-8 rounded-lg hover:bg-stone-100 grid place-items-center text-stone-600"><PencilSimple size={16}/></button>
              <button data-testid={ADMIN.categoryDeleteBtn(c.id)} onClick={() => remove(c)} className="w-8 h-8 rounded-lg hover:bg-red-50 grid place-items-center text-red-600"><Trash size={16}/></button>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 bg-stone-900/40 flex items-center justify-center p-4">
          <form onSubmit={submit} className="bg-white rounded-3xl border border-stone-200 w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-xl">{editing ? 'Edit Category' : 'New Category'}</h2>
              <button type="button" onClick={() => setShowForm(false)} className="w-8 h-8 rounded-full hover:bg-stone-100 grid place-items-center"><X size={18}/></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-stone-600">Name *</label>
                <input data-testid={ADMIN.categoryName} value={form.name} onChange={set('name')} className={inputCls} required/>
              </div>
              <div>
                <label className="text-xs font-medium text-stone-600">Description</label>
                <input data-testid={ADMIN.categoryDescription} value={form.description} onChange={set('description')} className={inputCls}/>
              </div>
              <div>
                <label className="text-xs font-medium text-stone-600">Image URL</label>
                <input data-testid={ADMIN.categoryImage} value={form.image_url} onChange={set('image_url')} className={inputCls} placeholder="https://..."/>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-stone-600">Sort order</label>
                  <input type="number" value={form.sort_order} onChange={set('sort_order')} className={inputCls}/>
                </div>
                <label className="flex items-end gap-2 text-sm pb-1"><input type="checkbox" checked={form.is_active} onChange={set('is_active')}/> Active</label>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={() => setShowForm(false)} className="h-10 px-5 rounded-full border border-stone-300 text-stone-700">Cancel</button>
              <button type="submit" data-testid={ADMIN.categorySaveBtn} disabled={busy} className="h-10 px-6 rounded-full bg-terracotta-600 text-white font-semibold hover:bg-terracotta-700 disabled:bg-stone-300">{busy ? 'Saving…' : 'Save'}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
